import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout, restoreSession } from './api';
import { session } from './session';
import type { Staff } from './types';

export interface AuthState {
  /** 'checking' while a stored session is verified on start-up. */
  status: 'checking' | 'ready';
  admin: Staff | null;
  isOwner: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const subscribe = (cb: () => void) => session.subscribe(cb);
const getAdmin = () => session.get()?.admin ?? null;

export function AuthProvider({ children, skipRestore = false }: { children: ReactNode; skipRestore?: boolean }) {
  const admin = useSyncExternalStore(subscribe, getAdmin, getAdmin);
  const [status, setStatus] = useState<'checking' | 'ready'>(() =>
    skipRestore || !session.get() ? 'ready' : 'checking',
  );

  useEffect(() => {
    if (status !== 'checking') return;
    let alive = true;
    restoreSession().finally(() => alive && setStatus('ready'));
    return () => {
      alive = false;
    };
  }, [status]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      admin,
      isOwner: admin?.role === 'OWNER',
      login: async (email, password) => {
        await apiLogin(email, password);
      },
      logout: () => apiLogout(),
    }),
    [status, admin],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
